import { useEffect, useRef } from "react";
import { actions, RichToolbar } from "react-native-pell-rich-editor";
import { StyleSheet, View } from "react-native";
import { RichEditor } from "react-native-pell-rich-editor";
import useFileUpload from "../../hooks/useFileUpload";
import { StoragePathType, useGetGcpSignedUrlForReadQuery } from "@axio/graphql/generated/graphql";

const MobileTextEditor = ({
  value,
  onChange,
  placeholder,
  activelyEditing,
  makeImagesPublic = false,
  restrictImageUpload = false,
}: {
  value: string;
  onChange: (content: string) => void;
  placeholder: string;
  activelyEditing: boolean;
  makeImagesPublic?: boolean;
  restrictImageUpload?: boolean;
}) => {
  const editorRef = useRef<RichEditor>(null);

  const { refetch: refetchGetGcpSignedUrlForRead } = useGetGcpSignedUrlForReadQuery({
    variables: {
      fileId: "",
      responseDisposition: "inline",
    },
  });

  const { handleFileUpload } = useFileUpload({
    storagePath: StoragePathType.RichTextEditorImages,
    onError: (message) => console.error("Image upload failed:", message),
    isPublic: makeImagesPublic,
  });

  useEffect(() => {
    if (value && editorRef.current && !activelyEditing) {
      editorRef.current.setContentHTML(value);
    }
  }, [value, editorRef.current, activelyEditing]);

  const handleImageUpload = async () => {
    const result = await handleFileUpload();
    if (result && result.length > 0) {
      const data = await refetchGetGcpSignedUrlForRead({
        fileId: result[0].file.id,
        responseDisposition: "inline",
      });
      if (data?.data?.getGcpSignedUrlForRead?.data?.length > 0) {
        const signedUrl = data.data.getGcpSignedUrlForRead.data[0].signedUrl;
        editorRef.current?.insertHTML(`<img data-file-id="axio-fileid://${result[0].file.id}" src="${signedUrl}"/>`);
      }
    }
  };

  const richToolbarActions = [
    actions.setBold,
    actions.setItalic,
    actions.setUnderline,
    actions.setStrikethrough,
    actions.heading1,
    actions.heading2,
    actions.insertBulletsList,
    actions.insertOrderedList,
    actions.keyboard,
    actions.alignLeft,
    actions.alignCenter,
    actions.alignRight,
    actions.insertLink,
  ];

  if (!restrictImageUpload) {
    richToolbarActions.push(actions.insertImage);
  }

  return (
    <View style={styles.richEditorContainer}>
      <RichEditor
        style={styles.richEditor}
        ref={editorRef}
        initialContentHTML={value}
        onChange={(content) => {
          onChange(content);
        }}
        placeholder={placeholder}
      />
      <RichToolbar
        editor={editorRef}
        onPressAddImage={handleImageUpload}
        actions={richToolbarActions}
        style={styles.richToolbar}
      />
    </View>
  );
};

export default MobileTextEditor;

const styles = StyleSheet.create({
  richEditorContainer: {
    borderWidth: 1,
    borderColor: "#D1D5DB",
    borderRadius: 8,
    overflow: "hidden",
  },
  richEditor: {
    minHeight: 200,
    maxHeight: 400,
  },
  richToolbar: {
    borderTopWidth: 1,
    borderTopColor: "#D1D5DB",
    backgroundColor: "#F9FAFB",
  },
});
